import * as z from 'zod';
import { BondRequestExtractionOutputFull, DocumentTypeField } from '../bond-request/bond-request-extraction-output.mjs';

const YesNo = z.enum(["Yes", "No"]).nullish();
const YesNoFinding = z.object({
    explanation: z.string().nullable(),
    value: YesNo,
});
const ContractPartySchema = z.object({
    name: z.string().nullable(),
    partyDesignation: z.string().nullable(),
});
const PrincipalRoleSchema = z.enum(["General Contractor", "Subcontractor", "Supplier"]);
const ObligeeRoleSchema = z.enum(["Owner", "General Contractor", "Subcontractor"]);
const ObligeeClassificationSchema = z.enum([
    "Public Entity",
    "Private Entity",
    "Federal",
    "State",
    "Municipal",
    "Tribal/Sovereign",
    "Quasi-Public",
]);
const TriageFlagsSchema = z.object({
    liquidatedDamages: z.boolean(),
    primeDamages: z.boolean(),
    actualDelayDamages: z.boolean(),
    lossOfUseDamages: z.boolean(),
    consequentialDelayDamages: z.boolean(),
    consequentialNonDelayDamages: z.boolean(),
    designBuild: z.boolean(),
    contractTakeover: z.boolean(),
    advancedPayment: z.boolean(),
    efficiencyGuarantee: z.boolean(),
    environmentalRemediation: z.boolean(),
    sovereignNations: z.boolean(),
    superfund: z.boolean(),
});
const TriageOutput = z.object({
    documentType: DocumentTypeField.optional(),
    parties: z.object({
        principal: ContractPartySchema,
        obligee: ContractPartySchema,
        principalRole: PrincipalRoleSchema.nullish(),
        obligeeRole: ObligeeRoleSchema.nullish(),
        obligeeClassification: ObligeeClassificationSchema.nullable(),
        owner: z.string().nullable(),
        otherUpstreamParties: z.array(z.string()).nullable(),
    }),
    triage: TriageFlagsSchema,
    document: z.object({
        standardContract: z.string().nullable(),
        referencesOtherContracts: YesNoFinding,
        fullContractProvided: YesNoFinding,
        signedByObligee: YesNoFinding,
        signedByPrincipal: YesNo,
        effectiveDate: z.string().nullable(),
        executionDate: z.string().nullable(),
        bidDate: z.string().nullable(),
    }),
    project: z.object({
        name: z.string().nullable(),
        projectNumber: BondRequestExtractionOutputFull.shape.projectNumber.optional(),
        description: z.string().nullable(),
        scope: z.string().nullable(),
        scopeOfWork: BondRequestExtractionOutputFull.shape.scopeOfWork.optional(),
        expectedStartDate: z.string().nullable(),
        expectedCompletionDate: z.string().nullable(),
    }),
});
const ContractTypeFinding = z.object({
    explanation: z.string().nullish(),
    value: YesNo,
});
const ContractTypeSchema = z.object({
    subcontract: ContractTypeFinding,
    supplyContract: ContractTypeFinding,
    supplyAndInstallContract: ContractTypeFinding,
    installContract: ContractTypeFinding,
    serviceContract: ContractTypeFinding,
    specialtyContract: ContractTypeFinding,
    generalContract: ContractTypeFinding,
    otherContract: ContractTypeFinding.extend({
        description: z.string().nullish(),
    }),
});
const AddressSchema = z.object({
    siteSpecificWork: z.string().nullish(),
    jobSiteAddress: z.string().nullish(),
    jobSiteCity: z.string().nullish(),
    jobSiteState: z.string().nullish(),
    jobSiteZip: z.string().nullish(),
});
const CompletionMilestoneSchema = z.object({
    date: z.string().nullish(),
    triggeringEvent: z.string().nullish(),
});
const ContractCoreTimelineSchema = z.object({
    explanation: z.string().nullish(),
    timeToCompleteMonths: BondRequestExtractionOutputFull.shape.timeToCompleteMonths.optional(),
    startDate: CompletionMilestoneSchema.nullish(),
    noticeToProceedDate: z.string().nullish(),
    awardDate: z.string().nullish(),
    substantialCompletion: CompletionMilestoneSchema.nullish(),
    finalCompletion: CompletionMilestoneSchema.nullish(),
    endDate: CompletionMilestoneSchema.nullish(),
    renewableContract: z
        .object({
        isRenewable: YesNo,
        renewalTerms: z.string().nullable(),
        partyConsentNeeded: YesNo,
    })
        .nullish(),
});
const ContractCoreOutput = z.object({
    description: z.object({
        tasksAndServices: z.string().nullish(),
        contractType: ContractTypeSchema,
        addresses: z.array(AddressSchema),
    }),
    value: z.object({
        contractValue: z.object({
            explanation: z.string().nullish(),
            value: z.number().nullish(),
        }),
        bestGuess: z.object({
            explanation: z.string().nullish(),
            value: z.number().nullish(),
        }),
        priceType: z.string().nullish(),
        rideAlong: z.object({
            explanation: z.string().nullish(),
            value: YesNo,
        }),
    }),
    timeline: ContractCoreTimelineSchema.nullish(),
    payment: z
        .object({
        terms: z
            .object({
            explanation: z.string().nullish(),
            payIfPaid: YesNo,
            payWhenPaid: YesNo,
            payWhenDue: YesNo,
            other: YesNo,
            otherDescription: z.string().nullish(),
        })
            .nullish(),
        timing: z
            .array(z.object({
            explanation: z.string().nullish(),
            daysCount: z.string().nullish(),
            daysType: z.string().nullish(),
            eventTriggeringPayment: z.string().nullish(),
        }))
            .nullish(),
        retainage: z
            .object({
            explanation: z.string().nullish(),
            description: z.string().nullable(),
        })
            .nullish(),
        structure: z
            .object({
            progressPayments: z.enum(["Monthly", "Other"]).nullable(),
            milestonePayments: z.enum(["When Met", "Other"]).nullable(),
            completionPayment: z
                .object({
                explanation: z.string().nullish(),
                applicable: YesNo,
                condition: z.string().nullish(),
            })
                .nullish(),
        })
            .nullish(),
    })
        .nullish(),
});
const DurationTypeSchema = z.enum(["business", "working", "calendar", "other"]).nullable();
const LegalStatuteSchema = z.object({
    identified: z.boolean(),
    relevantLegalStatute: z.string().nullable(),
});
const ExternalInfluenceSchema = z.object({
    explanation: z.string(),
    primeContract: z.boolean(),
    legalStatute: LegalStatuteSchema,
});
const WorkmanshipWarrantySchema = z.object({
    explanation: z.string(),
    identified: z.boolean(),
    confidence: z.number(),
    durationDays: z.number().nullable(),
    durationDaysType: DurationTypeSchema,
    durationMonths: z.number().nullable(),
    durationYears: z.number().nullable(),
    durationSentinel: z.literal("Yes, no duration explicitly mentioned").nullable(),
    externalInfluences: z.array(ExternalInfluenceSchema),
});
const MaterialsWarrantySchema = WorkmanshipWarrantySchema.extend({
    responsibleParty: z.enum(["Contractor", "Manufacturer/Supplier"]).nullable(),
});
const TreasuryListingSchema = z.object({
    explanation: z.string().nullish(),
    required: z.enum(["Yes", "No"]).nullish(),
});
const RatingRequirementSchema = z.object({
    explanation: z.string().nullish(),
    rating: z.string().nullish(),
});
const SizeRequirementSchema = z.object({
    explanation: z.string().nullish(),
    size: z.string().nullish(),
});
const SuretyRequirementSchema = z.object({
    contractParties: z.object({
        principal: z.object({ name: z.string().nullish() }),
        obligee: z.object({ name: z.string().nullish() }),
    }),
    treasuryListing: z.object({
        insuranceProvider: TreasuryListingSchema,
        surety: TreasuryListingSchema,
    }),
    ratings: z.object({
        insuranceProvider: RatingRequirementSchema,
        surety: RatingRequirementSchema,
    }),
    sizeRequirements: z.object({
        insuranceProvider: SizeRequirementSchema,
        surety: SizeRequirementSchema,
    }),
    isCompletionBond: z.object({
        explanation: z.string().nullish(),
        required: z.enum(["Yes", "No"]).nullish(),
    }),
    bondAmount: z.object({
        explanation: z.string().nullish(),
        amount: z.number().nullish(),
        bondAmountAsPercentOfBid: BondRequestExtractionOutputFull.shape.bondAmountAsPercentOfBid.optional(),
        description: z.string().nullish(),
    }),
    bondForm: z.object({
        explanation: z.string().nullish(),
        provided: z.enum(["Yes", "No"]).nullish(),
        formName: z.string().nullish(),
        finalBondFormType: BondRequestExtractionOutputFull.shape.finalBondFormType.optional(),
    }),
    expectedPremium: z.object({
        explanation: z.string().nullish(),
        amount: z.number().nullish(),
        description: z.string().nullish(),
    }),
    actionsInDefault: z.object({
        explanation: z.string().nullish(),
        description: z.string().nullish(),
    }),
});
const WarrantiesOutput = z.object({
    workmanshipWarranties: z.array(WorkmanshipWarrantySchema),
    materialsWarranties: z.array(MaterialsWarrantySchema),
    warrantyPeriodMonths: BondRequestExtractionOutputFull.shape.warrantyPeriodMonths.optional(),
    surety: z.array(SuretyRequirementSchema),
    defaultProvisions: z
        .object({
        groundsToDefault: z.string().nullable(),
        processForDefaulting: z.string().nullable(),
    })
        .nullable(),
});
const LiquidatedDamagesClauseSubsetSchema = z.object({
    text: z.string(),
    liquidatedDamagesPerDay: z.number().nullable(),
    clearlyMoreThanFiveThousand: z.boolean(),
});
const LiquidatedDamagesClauseSchema = z.object({
    clauseLocation: z.string(),
    subsets: z.array(LiquidatedDamagesClauseSubsetSchema),
});
const LiquidatedDamagesOutput = z.object({
    explanation: z.string(),
    amountDescription: z.string().nullable(),
    clauses: z.array(LiquidatedDamagesClauseSchema),
    identified: z.boolean(),
});
const ClauseSchema = z.object({
    clauseLocation: z.string(),
    summary: z.string(),
    explanation: z.string().nullish(),
});
const ReversePassThroughEnum = z
    .enum(["Not Applicable to All", "Partially Applicable", "Applicable to All"])
    .nullable();
const WaiverEnum = z
    .enum(["All Reclassified", "Some Reclassified", "None Reclassified"])
    .nullable();
const CONSEQUENTIAL_PATHWAY_PRIORITY = [
    "explicit",
    "implied",
    "impliedBroadIndemnityOnly",
];
const consequentialDamageFindingShape = z.object({
    explanation: z.string(),
    explicit: z.boolean().nullish(),
    implied: z.boolean().nullish(),
    impliedBroadIndemnityOnly: z.boolean().nullish(),
    reversePassThrough: ReversePassThroughEnum,
    waiver: WaiverEnum,
    identified: z.boolean(),
});
const ConsequentialDamageFindingSchema = z.codec(consequentialDamageFindingShape, consequentialDamageFindingShape, {
    decode: (finding) => {
        const winner = CONSEQUENTIAL_PATHWAY_PRIORITY.find((key) => finding[key] === true);
        if (winner === undefined)
            return finding;
        return {
            ...finding,
            explicit: winner === "explicit",
            implied: winner === "implied",
            impliedBroadIndemnityOnly: winner === "impliedBroadIndemnityOnly",
        };
    },
    encode: (finding) => finding,
});
const ConsequentialDamagesOutput = z.object({
    prime: z.object({
        explanation: z.string(),
        clauses: z.array(ClauseSchema),
        identified: z.boolean(),
    }),
    actualDelay: z.object({
        explanation: z.string(),
        clauses: z.array(ClauseSchema),
        identified: z.boolean(),
    }),
    lossOfUse: z.object({
        explanation: z.string(),
        identified: z.boolean(),
    }),
    consequentialDelay: ConsequentialDamageFindingSchema,
    consequentialNonDelay: ConsequentialDamageFindingSchema,
    otherDelayDamages: z.object({
        explanation: z.string().nullable(),
        identified: z.boolean(),
    }),
});
const RiskFindingSchema = z.object({
    explanation: z.string(),
    identified: z.boolean(),
});
const SpecialRisksOutput = z.object({
    designBuild: RiskFindingSchema,
    contractTakeover: RiskFindingSchema,
    advancedPayment: RiskFindingSchema.extend({
        allowsAdvancePayments: z.string().nullish(),
        amountDescription: z.string().nullable(),
    }),
    efficiencyGuarantee: RiskFindingSchema.extend({
        isPrimeContractor: z.boolean().nullable(),
    }),
    environmentalRemediation: RiskFindingSchema,
    sovereignNations: RiskFindingSchema,
    superfund: RiskFindingSchema,
    otherHazard: z.string().nullable(),
});
const ContractExtractionOutput = z.object({
    triage: TriageOutput.optional(),
    contractCore: ContractCoreOutput.optional(),
    warranties: WarrantiesOutput.optional(),
    liquidatedDamages: LiquidatedDamagesOutput.optional(),
    consequentialDamages: ConsequentialDamagesOutput.optional(),
    specialRisks: SpecialRisksOutput.optional(),
    notes: z.array(z.string()).optional(),
});

export { CONSEQUENTIAL_PATHWAY_PRIORITY, ConsequentialDamagesOutput, ContractCoreOutput, ContractCoreTimelineSchema, ContractExtractionOutput, LiquidatedDamagesOutput, ObligeeClassificationSchema, ObligeeRoleSchema, PrincipalRoleSchema, SpecialRisksOutput, SuretyRequirementSchema, TriageFlagsSchema, TriageOutput, WarrantiesOutput };
